import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useRouteMatch } from "react-router-dom";

import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { EntityType, FileType } from "constants/index";
import { Routes } from "constants/routes";
import { useActionHook, useNavigate, useNavigationHooks, usePrompt, useUrlHooks } from "hooks";
import { useDealActions, useDocumentsActions, useUserActions, useWorkspaceColumnsSelector } from "store";
import {
  DealBase,
  IFile,
  IFileMetaDetail,
  IFileRepo,
  IFolder,
  IMetaDetails,
  IWorkspaceMetadata,
  PatchFolderMetaDetailPayload,
  WorkspaceFilters,
  WorkspaceFiltersFromDB
} from "store/types";

/** Returns ancestor folder ids (path from root to target, excluding target) so the path is expanded. */
function getAncestorFolderIds(folders: IFolder[], targetFolderId: number): number[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const path: number[] = [];
  let id: number | null = targetFolderId;
  while (id != null) {
    const folder = byId.get(id);
    if (!folder) break;
    path.push(folder.id);
    id = folder.parent_id;
  }
  path.reverse();
  return path.slice(0, -1);
}

export interface WorkspaceContextValue {
  deals: DealBase[];
  globalFolders: IFolder[];
  foldersByDeal: Map<number, IFolder[]>;
  /** Returns folders for the deal (from cache or after fetching). Async so callers can track loading. */
  getOrLoadDealFolders: (dealId: number) => Promise<IFolder[]>;
  addDealFolder: (data: { dealId: number; folder: IFolder }) => void;
  removeDealFolder: (data: { dealId: number; folderId: number }) => void;
  addGlobalFolder: (data: { folder: IFolder }) => void;
  removeGlobalFolder: (data: { folderId: number }) => void;
  patchFolder: (data: { dealId: number | null; folderId: number; patch: Partial<IFolder> }) => void;
  openedDealIds: Set<number>;
  setOpenedDealIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  openedFolderIds: Set<number>;
  setOpenedFolderIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  /** Deal id from URL (when on workspace deal or deal folder route). */
  currentDealId: number | null;
  /** Folder id from URL (when on workspace deal folder route). */
  currentFolderId: number | null;
  /** Resolved current folder from foldersByDeal when currentDealId and currentFolderId are set. */
  currentFolder: IFolder | null;
  activeFilters: WorkspaceFilters;
  handleChangeFilter: (filters: WorkspaceFilters) => void;
  /** True once the user has explicitly changed filters; used to suppress re-adding default filter keys on remount. */
  filtersUserInteracted: boolean;
  /** Build API payload from activeFilters for getFolderMetadataAction. */
  buildFilterPayload: (filters: WorkspaceFilters) => Record<string, unknown>;

  getFolderMetadata: (params?: { recently_added?: boolean }) => Promise<void>;
  folderMetadataLoading: boolean;
  folderMetadata: IWorkspaceMetadata | null;
  handleFetchNextPage: () => void;

  globalFolderFilters: WorkspaceFiltersFromDB;
  globalFolderFiltersLoading: boolean;
  refetchFolderMetadata: () => Promise<void>;
  refetchFolders: () => Promise<void>;
  /** Force-reload folders for a specific deal (bypasses cache). */
  reloadDealFolders: (dealId: number) => Promise<void>;
  // Column visibility
  columnVisibility: Record<string, boolean>;
  toggleColumn: (columnId: string) => void;

  // Column order
  columnOrder: string[];
  reorderColumns: (order: string[]) => void;

  // Selection
  selectedItems: IMetaDetails[];
  setSelectedItems: React.Dispatch<React.SetStateAction<IMetaDetails[]>>;

  // Row click
  handleRowClick: (row: IMetaDetails) => void;
  previewFile: IFile | null;
  setPreviewFile: React.Dispatch<React.SetStateAction<IFile | null>>;

  // Deal filter
  selectedDeals: DealBase[];
  setSelectedDeals: (deals: DealBase[]) => void;

  // Dispatch a detail mutation (patch or delete) on local folderMetadata state
  dispatchDetail: (payload: PatchFolderMetaDetailPayload) => void;

  // Server-side sorting
  sortBy: { sort_by: string; sort: "asc" | "desc" } | null;
  setSortBy: (sortBy: { sort_by: string; sort: "asc" | "desc" } | null) => void;
}

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    getDealsBaseAction,
    getFolderMetadataAction,
    getGlobalFolderFiltersAction,
    getGlobalWorkspaceFoldersAction,
    downloadFileFromDRAction
  } = useDealActions();
  const openPrompt = usePrompt();
  const { getDealFoldersAction } = useDocumentsActions();
  const { updateWorkspaceColumnsAction } = useUserActions();
  const workspaceColumns = useWorkspaceColumnsSelector();
  const { queryParams } = useUrlHooks();
  const { removeParams } = useNavigationHooks();

  const [foldersByDeal, setFoldersByDeal] = useState<Map<number, IFolder[]>>(new Map());
  const [openedDealIds, setOpenedDealIds] = useState<Set<number>>(() => new Set());
  const [openedFolderIds, setOpenedFolderIds] = useState<Set<number>>(() => new Set());
  const [activeFilters, setActiveFilters] = useState<WorkspaceFilters>({});
  const [filtersUserInteracted, setFiltersUserInteracted] = useState(false);

  // Column visibility
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(workspaceColumns);
  const toggleColumn = useCallback(
    (columnId: string) => {
      setColumnVisibility((prev) => {
        const next = { ...prev, [columnId]: !prev[columnId] };
        updateWorkspaceColumnsAction(next);
        return next;
      });
    },
    [updateWorkspaceColumnsAction]
  );

  // Column order
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const reorderColumns = useCallback((order: string[]) => {
    setColumnOrder(order);
  }, []);

  // Selection
  const [selectedItems, setSelectedItems] = useState<IMetaDetails[]>([]);

  // Deal filter
  const [selectedDeals, setSelectedDeals] = useState<DealBase[]>([]);

  // Server-side sorting
  const [sortBy, setSortBy] = useState<{ sort_by: string; sort: "asc" | "desc" } | null>(null);
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;

  // Route detection for global files
  const isRecentlyAddedRoute = !!useRouteMatch({ path: Routes.workspaceRecentlyAdded, exact: true });
  const isAllFilesRoute = !!useRouteMatch({ path: Routes.workspaceAllFiles, exact: true });
  const isGlobalFilesRoute = isRecentlyAddedRoute || isAllFilesRoute;

  const navigate = useNavigate();
  const [previewFile, setPreviewFile] = useState<IFile | null>(null);

  const {
    data: globalFolderFilters = {},
    action: getGlobalFolderFilters,
    loading: globalFolderFiltersLoading
  } = useActionHook(getGlobalFolderFiltersAction);

  useEffect(() => {
    getGlobalFolderFilters();
  }, []);

  const { deal_id: dealIdParam, folder_id: folderIdParam } = useParams<{ deal_id?: string; folder_id?: string }>();
  const dealIdFromUrl = dealIdParam != null ? Number(dealIdParam) : null;
  const folderIdFromUrl = folderIdParam != null ? Number(folderIdParam) : null;
  /** section_id to send to the API: -1 for all files / recently added, folder id or null otherwise. */
  const sectionIdForApi = isGlobalFilesRoute ? -1 : folderIdFromUrl;

  const { data: deals = [], action: getDeals } = useActionHook(getDealsBaseAction);
  const {
    data: globalFolders = [],
    action: getGlobalFolders,
    setData: setGlobalFolders
  } = useActionHook(getGlobalWorkspaceFoldersAction);

  const currentFolder = useMemo(() => {
    if (folderIdFromUrl == null) return null;
    if (dealIdFromUrl != null) {
      const folders = foldersByDeal.get(dealIdFromUrl) ?? [];
      return folders.find((f) => f.id === folderIdFromUrl) ?? null;
    }
    return globalFolders.find((f) => f.id === folderIdFromUrl) ?? null;
  }, [dealIdFromUrl, folderIdFromUrl, foldersByDeal, globalFolders]);

  const buildFilterPayload = useCallback(
    (filters: WorkspaceFilters): Record<string, unknown> => {
      const dateItem = filters?.last_updated?.[0];
      const dateRange =
        dateItem && typeof dateItem === "object" && "from" in dateItem
          ? (dateItem as { from: string; to: string })
          : null;
      return {
        investor_ids: filters?.investor?.map((investor) => (investor as { id: number }).id) || undefined,
        file_type: filters?.file_type,
        tags: filters?.tag?.map((tag) => (tag as { id: number }).id) || undefined,
        classification: filters?.classification,
        file_status: filters?.file_status,
        shared_with: filters?.shared_with?.[0] ? (filters?.shared_with?.[0] === "Yes" ? true : false) : undefined,
        document_type: filters?.document_type,
        ...(filters?.last_updated ? { last_updated: [{ from: dateRange?.from ?? "", to: dateRange?.to ?? "" }] } : {}),
        ...(currentFolder?.kind === "dataroom" ? { account_name: filters?.account_name } : {})
      };
    },
    [currentFolder?.kind]
  );

  const handleChangeFilter = useCallback((filters: WorkspaceFilters) => {
    setActiveFilters(filters);
    setFiltersUserInteracted(true);
  }, []);

  const getOrLoadDealFolders = useCallback<WorkspaceContextValue["getOrLoadDealFolders"]>(
    async (dealId) => {
      const folders = await getDealFoldersAction({ dealId, kind: "folder" });
      const foldersArray = Object.values(folders);
      setFoldersByDeal((prev) => {
        const next = new Map(prev);
        next.set(dealId, foldersArray);
        return next;
      });
      return foldersArray;
    },
    [getDealFoldersAction]
  );

  const addDealFolder = useCallback<WorkspaceContextValue["addDealFolder"]>((data) => {
    setFoldersByDeal((prev) => {
      const next = new Map(prev);
      next.set(data.dealId, [...(prev.get(data.dealId) || []), data.folder]);
      return next;
    });
  }, []);

  const removeDealFolder = useCallback<WorkspaceContextValue["removeDealFolder"]>((data) => {
    setFoldersByDeal((prev) => {
      const next = new Map(prev);
      const folders = prev.get(data.dealId) ?? [];
      next.set(
        data.dealId,
        folders.filter((f) => f.id !== data.folderId)
      );
      return next;
    });
  }, []);

  const addGlobalFolder = useCallback<WorkspaceContextValue["addGlobalFolder"]>((data) => {
    setGlobalFolders((prev) => {
      return [...(prev || []), data.folder];
    });
  }, []);

  const removeGlobalFolder = useCallback<WorkspaceContextValue["removeGlobalFolder"]>((data) => {
    setGlobalFolders((prev) => (prev || []).filter((f) => f.id !== data.folderId));
  }, []);

  const patchFolder = useCallback<WorkspaceContextValue["patchFolder"]>(({ dealId, folderId, patch }) => {
    if (dealId != null) {
      setFoldersByDeal((prev) => {
        const folders = prev.get(dealId);
        if (!folders) return prev;
        const next = new Map(prev);
        next.set(
          dealId,
          folders.map((f) => (f.id === folderId ? { ...f, ...patch } : f))
        );
        return next;
      });
    } else {
      setGlobalFolders((prev) => (prev || []).map((f) => (f.id === folderId ? { ...f, ...patch } : f)));
    }
  }, []);

  useEffect(() => {
    getDeals();
    getGlobalFolders();
  }, []);

  // Load deal folders when navigating to a deal (single source of truth for URL-driven fetch)
  useEffect(() => {
    if (dealIdFromUrl == null) return;
    if (foldersByDeal.has(dealIdFromUrl)) return;
    getOrLoadDealFolders(dealIdFromUrl);
  }, [dealIdFromUrl]);

  // --------------------Refetch from external trigger-------------------

  useEffect(() => {
    if (queryParams.refetchWorkspace) {
      refetchFolderMetadata();
      removeParams(["refetchWorkspace"]);
    }
  }, [queryParams.refetchWorkspace]);

  useEffect(() => {
    if (dealIdFromUrl == null || folderIdFromUrl == null) return;

    const folders = foldersByDeal.get(dealIdFromUrl) ?? [];
    const ancestorIds = getAncestorFolderIds(folders, folderIdFromUrl);
    if (ancestorIds.length === 0) return;

    setOpenedFolderIds((prev) => {
      if (ancestorIds.every((id) => prev.has(id))) return prev;
      const next = new Set([...Array.from(prev), ...ancestorIds]);
      return next;
    });
  }, [dealIdFromUrl, folderIdFromUrl, foldersByDeal]);

  useEffect(() => {
    if (dealIdFromUrl != null || folderIdFromUrl == null) return;

    const ancestorIds = getAncestorFolderIds(globalFolders, folderIdFromUrl);
    if (ancestorIds.length === 0) return;

    setOpenedFolderIds((prev) => {
      if (ancestorIds.every((id) => prev.has(id))) return prev;
      const next = new Set([...Array.from(prev), ...ancestorIds]);
      return next;
    });
  }, [folderIdFromUrl, dealIdFromUrl, globalFolders]);

  const [folderMetadata, setFolderMetadata] = useState<IWorkspaceMetadata | null>(null);
  const hasMoreRef = useRef(false);
  const pageRef = useRef(1);
  const [recentlyAdded, setRecentlyAdded] = useState<boolean | undefined>(undefined);
  const fetchingNextPageRef = useRef(false);

  const { action: getFolderMetadata, loading: folderMetadataLoading } = useActionHook(
    async (params?: { recently_added?: boolean }) => {
      pageRef.current = 1;
      hasMoreRef.current = false;
      fetchingNextPageRef.current = false;
      setFolderMetadata(null);
      setRecentlyAdded(params?.recently_added);
      const metadata = await getFolderMetadataAction({
        dealId: dealIdFromUrl,
        sectionId: sectionIdForApi,
        filters: {
          ...(buildFilterPayload(activeFilters) as WorkspaceFilters),
          ...(selectedDeals.length && isGlobalFilesRoute ? { deal_ids: selectedDeals.map((d) => d.id) } : {})
        },
        disableDispatch: true,
        recently_added: params?.recently_added,
        page: 1,
        ...(sortByRef.current ? sortByRef.current : {})
      });

      setFolderMetadata(metadata);
      hasMoreRef.current = pageRef.current < (metadata.last_page ?? 1);
    }
  );

  const handleFetchNextPage = useCallback(async () => {
    if (fetchingNextPageRef.current || !hasMoreRef.current) return;
    fetchingNextPageRef.current = true;
    const nextPage = pageRef.current + 1;
    try {
      const metadata = await getFolderMetadataAction({
        dealId: dealIdFromUrl,
        sectionId: sectionIdForApi,
        filters: {
          ...(buildFilterPayload(activeFilters) as WorkspaceFilters),
          ...(selectedDeals.length && isGlobalFilesRoute ? { deal_ids: selectedDeals.map((d) => d.id) } : {})
        },
        disableDispatch: true,
        recently_added: recentlyAdded,
        page: nextPage,
        ...(sortByRef.current ? sortByRef.current : {})
      });
      pageRef.current = nextPage;
      if (!metadata.details?.length) {
        hasMoreRef.current = false;
        return;
      }
      setFolderMetadata((prev) => (prev ? { ...prev, details: [...prev.details, ...metadata.details] } : metadata));
      hasMoreRef.current = pageRef.current < (metadata.last_page ?? 1);
    } catch {
      hasMoreRef.current = false;
    } finally {
      fetchingNextPageRef.current = false;
    }
  }, [
    recentlyAdded,
    dealIdFromUrl,
    sectionIdForApi,
    buildFilterPayload,
    activeFilters,
    selectedDeals,
    getFolderMetadataAction
  ]);

  // Auto-fetch for global files routes (All Files / Recently Added)
  useEffect(() => {
    if (!isGlobalFilesRoute) return;
    setSelectedItems([]);
    getFolderMetadata({ recently_added: isRecentlyAddedRoute });
  }, [isGlobalFilesRoute, isRecentlyAddedRoute, activeFilters, selectedDeals, sortBy]);

  const reloadDealFolders = useCallback(
    async (dealId: number) => {
      const folders = await getDealFoldersAction({ dealId, kind: "folder" });
      const foldersArray = Object.values(folders);
      setFoldersByDeal((prev) => {
        const next = new Map(prev);
        next.set(dealId, foldersArray);
        return next;
      });
    },
    [getDealFoldersAction]
  );

  const refetchFolders = useCallback(async () => {
    await getGlobalFolders();
    if (dealIdFromUrl == null) return;
    const folders = await getDealFoldersAction({ dealId: dealIdFromUrl, kind: "folder" });
    const foldersArray = Object.values(folders);
    setFoldersByDeal((prev) => {
      const next = new Map(prev);
      next.set(dealIdFromUrl, foldersArray);
      return next;
    });
  }, [dealIdFromUrl, getDealFoldersAction, getGlobalFolders]);

  const refetchFolderMetadata = useCallback(async () => {
    pageRef.current = 1;
    hasMoreRef.current = false;
    fetchingNextPageRef.current = false;
    try {
      const metadata = await getFolderMetadataAction({
        dealId: dealIdFromUrl,
        sectionId: sectionIdForApi,
        filters: {
          ...(buildFilterPayload(activeFilters) as WorkspaceFilters),
          ...(selectedDeals.length && isGlobalFilesRoute ? { deal_ids: selectedDeals.map((d) => d.id) } : {})
        },
        recently_added: recentlyAdded,
        page: 1,
        ...(sortByRef.current ? sortByRef.current : {})
      });
      setFolderMetadata(metadata);
      hasMoreRef.current = pageRef.current < (metadata.last_page ?? 1);
    } finally {
      await refetchFolders();
    }
  }, [
    dealIdFromUrl,
    sectionIdForApi,
    activeFilters,
    buildFilterPayload,
    getFolderMetadataAction,
    selectedDeals,
    recentlyAdded,
    refetchFolders
  ]);

  const dispatchDetail = useCallback((payload: PatchFolderMetaDetailPayload) => {
    setFolderMetadata((prev) => {
      if (!prev?.details) return prev;
      const details =
        payload.key === "delete"
          ? prev.details.filter((item) => !(item.id === payload.id && item.entity_type === payload.entityType))
          : prev.details.map((item) =>
              item.id === payload.id && item.entity_type === payload.entityType
                ? ({ ...item, ...payload.patch } as IMetaDetails)
                : item
            );
      return { ...prev, details };
    });
  }, []);

  const PREVIEW_TYPES = [FileType.PDF, FileType.JPG, FileType.JPEG, FileType.PNG];

  const handleRowClick = useCallback(
    async (row: IMetaDetails) => {
      const fileDetail = row as IFileMetaDetail;
      const fileDealId = fileDetail.deal?.id;
      const dataroomFileId = fileDetail.dataroom_file_id;

      if (row.entity_type === EntityType.FILE && dataroomFileId && fileDetail.file_url === "" && fileDealId) {
        const syncedFile = await downloadFileFromDRAction({
          dealId: fileDealId,
          fileId: dataroomFileId
        });
        dispatchDetail({
          key: "patch",
          id: row.id,
          entityType: EntityType.FILE,
          patch: { file_url: syncedFile.file_url ?? "" }
        });
        return;
      }

      if (row.type === EntityType.FOLDER) {
        if (row.deal_id) {
          navigate({ route: "workspaceDealFolder", params: { deal_id: row.deal_id, folder_id: row.id } });
        } else {
          navigate({ route: "workspaceFolder", params: { folder_id: row.id } });
        }
        return;
      }

      if (row.type === FileType.PDF) {
        openPrompt({
          componentType: "fullscreen",
          Component: DataExtractionModal,

          props: {
            fileIds: [row.id],
            fileNames: { [row.id]: row.name },
            source: "globalWorkspace" as const,
            initialShowParameters: false,
            onUpdateFile: (fileId: number, updates: Partial<IFileRepo>) => {
              dispatchDetail({
                key: "patch",
                id: fileId,
                entityType: EntityType.FILE,
                patch: updates
              });
            }
          }
        });
        return;
      }

      if (PREVIEW_TYPES.includes(row.type as FileType)) {
        setPreviewFile(row as unknown as IFile);
        return;
      }

      if (row.type === EntityType.DOCUMENT) {
        if (row.deal_id) {
          navigate({
            route: "workspaceDealFolderDocument",
            params: { deal_id: row.deal_id, folder_id: row.section_id, document_id: row.id },
            redirectMethod: "push",
            setQueriesMethod: "drop"
          });
        } else {
          navigate({
            route: "workspaceFolderDocument",
            params: { folder_id: row.section_id, document_id: row.id },
            redirectMethod: "push",
            setQueriesMethod: "drop"
          });
        }
        return;
      }

      if (row.type === EntityType.FORM) {
        if (row.deal_id) {
          navigate({
            route: "workspaceDealFolderForm",
            params: { deal_id: row.deal_id, folder_id: row.section_id, form_id: row.id },
            redirectMethod: "push",
            setQueriesMethod: "drop"
          });
        } else {
          navigate({
            route: "workspaceFolderForm",
            params: { folder_id: row.section_id, form_id: row.id },
            redirectMethod: "push",
            setQueriesMethod: "drop"
          });
        }
      }
    },
    [dealIdFromUrl, folderIdFromUrl, navigate]
  );

  return (
    <WorkspaceContext.Provider
      value={{
        deals,
        globalFolders,
        foldersByDeal,
        getOrLoadDealFolders,
        addDealFolder,
        addGlobalFolder,
        removeGlobalFolder,
        removeDealFolder,
        patchFolder,
        openedDealIds,
        setOpenedDealIds,
        openedFolderIds,
        setOpenedFolderIds,
        currentDealId: dealIdFromUrl,
        currentFolderId: folderIdFromUrl,
        currentFolder,
        activeFilters,
        filtersUserInteracted,
        globalFolderFilters,
        globalFolderFiltersLoading,
        handleChangeFilter,
        buildFilterPayload,
        getFolderMetadata,
        folderMetadataLoading,
        folderMetadata,
        columnVisibility,
        toggleColumn,
        columnOrder,
        reorderColumns,
        selectedItems,
        setSelectedItems,
        refetchFolderMetadata,
        refetchFolders,
        reloadDealFolders,
        dispatchDetail,
        handleFetchNextPage,
        handleRowClick,
        previewFile,
        setPreviewFile,
        selectedDeals,
        setSelectedDeals,
        sortBy,
        setSortBy
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

const WorkspaceContext = createContext<WorkspaceContextValue>({} as WorkspaceContextValue);
export const useWorkspace = () => useContext<WorkspaceContextValue>(WorkspaceContext);
